/**
 * Static list of common exercises for autocomplete in workout logging.
 * Organized by muscle group with equipment and category metadata.
 */

export const EXERCISE_CATEGORIES = {
  CHEST: 'Chest',
  BACK: 'Back',
  SHOULDERS: 'Shoulders',
  BICEPS: 'Biceps',
  TRICEPS: 'Triceps',
  LEGS: 'Legs',
  CORE: 'Core',
  CARDIO: 'Cardio',
  FULL_BODY: 'Full Body',
  FLEXIBILITY: 'Flexibility',
  OLYMPIC: 'Olympic',
};

/**
 * Equipment types for exercises.
 */
export const EQUIPMENT_TYPES = {
  BARBELL: 'barbell',
  DUMBBELL: 'dumbbell',
  MACHINE: 'machine',
  BODYWEIGHT: 'bodyweight',
  CABLE: 'cable',
  KETTLEBELL: 'kettlebell',
  OTHER: 'other',
};

/**
 * Exercise type categories.
 */
export const EXERCISE_TYPES = {
  COMPOUND: 'compound',
  ISOLATION: 'isolation',
};

/**
 * Common exercises list with name, muscle group, equipment, and category type.
 * Used by MUI Autocomplete with freeSolo to allow custom entries.
 */
export const COMMON_EXERCISES = [
  // Chest
  { name: 'Bench Press', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Incline Bench Press', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Decline Bench Press', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Dumbbell Bench Press', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Incline Dumbbell Press', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Chest Fly', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Cable Crossover', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.CABLE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Push-Up', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Dips (Chest)', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Pec Deck', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Machine Chest Press', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Landmine Press', category: EXERCISE_CATEGORIES.CHEST, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },

  // Back
  { name: 'Deadlift', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Pull-Up', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Chin-Up', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Barbell Row', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Dumbbell Row', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Seated Cable Row', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.CABLE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Lat Pulldown', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.CABLE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'T-Bar Row', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Face Pull', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.CABLE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Hyperextension', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Pendlay Row', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Meadows Row', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Chest Supported Row', category: EXERCISE_CATEGORIES.BACK, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },

  // Shoulders
  { name: 'Overhead Press', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Military Press', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Dumbbell Shoulder Press', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Arnold Press', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Lateral Raise', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Front Raise', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Reverse Fly', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Upright Row', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Shrugs', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Cable Lateral Raise', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.CABLE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Machine Shoulder Press', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Lu Raise', category: EXERCISE_CATEGORIES.SHOULDERS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },

  // Biceps
  { name: 'Barbell Curl', category: EXERCISE_CATEGORIES.BICEPS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Dumbbell Curl', category: EXERCISE_CATEGORIES.BICEPS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Hammer Curl', category: EXERCISE_CATEGORIES.BICEPS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Preacher Curl', category: EXERCISE_CATEGORIES.BICEPS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Concentration Curl', category: EXERCISE_CATEGORIES.BICEPS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Cable Curl', category: EXERCISE_CATEGORIES.BICEPS, equipment: EQUIPMENT_TYPES.CABLE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Incline Dumbbell Curl', category: EXERCISE_CATEGORIES.BICEPS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'EZ Bar Curl', category: EXERCISE_CATEGORIES.BICEPS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Spider Curl', category: EXERCISE_CATEGORIES.BICEPS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },

  // Triceps
  { name: 'Tricep Pushdown', category: EXERCISE_CATEGORIES.TRICEPS, equipment: EQUIPMENT_TYPES.CABLE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Overhead Tricep Extension', category: EXERCISE_CATEGORIES.TRICEPS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Skull Crushers', category: EXERCISE_CATEGORIES.TRICEPS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Close-Grip Bench Press', category: EXERCISE_CATEGORIES.TRICEPS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Dips (Triceps)', category: EXERCISE_CATEGORIES.TRICEPS, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Wrist Curl', category: EXERCISE_CATEGORIES.TRICEPS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Tricep Kickback', category: EXERCISE_CATEGORIES.TRICEPS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Diamond Push-Up', category: EXERCISE_CATEGORIES.TRICEPS, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Cable Overhead Extension', category: EXERCISE_CATEGORIES.TRICEPS, equipment: EQUIPMENT_TYPES.CABLE, type: EXERCISE_TYPES.ISOLATION },

  // Legs
  { name: 'Squat', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Front Squat', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Goblet Squat', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Leg Press', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Lunges', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Bulgarian Split Squat', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Romanian Deadlift', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Leg Curl', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Leg Extension', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Calf Raise', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Hip Thrust', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Hack Squat', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Step-Up', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Sumo Deadlift', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Glute Bridge', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Walking Lunge', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Seated Calf Raise', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Good Morning', category: EXERCISE_CATEGORIES.LEGS, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },

  // Core
  { name: 'Plank', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Crunch', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Sit-Up', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Russian Twist', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Leg Raise', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Hanging Leg Raise', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Ab Wheel Rollout', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Cable Woodchop', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.CABLE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Dead Bug', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Mountain Climber', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Bicycle Crunch', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Pallof Press', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.CABLE, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Side Plank', category: EXERCISE_CATEGORIES.CORE, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },

  // Cardio
  { name: 'Running', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Treadmill', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Cycling', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Stationary Bike', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Rowing Machine', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Elliptical', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Jump Rope', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Swimming', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Stair Climber', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.MACHINE, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Walking', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Sprints', category: EXERCISE_CATEGORIES.CARDIO, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.COMPOUND },

  // Full Body
  { name: 'Burpee', category: EXERCISE_CATEGORIES.FULL_BODY, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Kettlebell Swing', category: EXERCISE_CATEGORIES.FULL_BODY, equipment: EQUIPMENT_TYPES.KETTLEBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Turkish Get-Up', category: EXERCISE_CATEGORIES.FULL_BODY, equipment: EQUIPMENT_TYPES.KETTLEBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Battle Ropes', category: EXERCISE_CATEGORIES.FULL_BODY, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Box Jump', category: EXERCISE_CATEGORIES.FULL_BODY, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Thruster', category: EXERCISE_CATEGORIES.FULL_BODY, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Wall Ball', category: EXERCISE_CATEGORIES.FULL_BODY, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Man Maker', category: EXERCISE_CATEGORIES.FULL_BODY, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Farmer Walk', category: EXERCISE_CATEGORIES.FULL_BODY, equipment: EQUIPMENT_TYPES.DUMBBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Sled Push', category: EXERCISE_CATEGORIES.FULL_BODY, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.COMPOUND },

  // Flexibility
  { name: 'Yoga Flow', category: EXERCISE_CATEGORIES.FLEXIBILITY, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Foam Rolling', category: EXERCISE_CATEGORIES.FLEXIBILITY, equipment: EQUIPMENT_TYPES.OTHER, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Static Stretching', category: EXERCISE_CATEGORIES.FLEXIBILITY, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.ISOLATION },
  { name: 'Dynamic Stretching', category: EXERCISE_CATEGORIES.FLEXIBILITY, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Pilates', category: EXERCISE_CATEGORIES.FLEXIBILITY, equipment: EQUIPMENT_TYPES.BODYWEIGHT, type: EXERCISE_TYPES.COMPOUND },

  // Olympic
  { name: 'Clean and Jerk', category: EXERCISE_CATEGORIES.OLYMPIC, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Snatch', category: EXERCISE_CATEGORIES.OLYMPIC, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Power Clean', category: EXERCISE_CATEGORIES.OLYMPIC, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Hang Clean', category: EXERCISE_CATEGORIES.OLYMPIC, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Push Press', category: EXERCISE_CATEGORIES.OLYMPIC, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Clean Pull', category: EXERCISE_CATEGORIES.OLYMPIC, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
  { name: 'Hang Snatch', category: EXERCISE_CATEGORIES.OLYMPIC, equipment: EQUIPMENT_TYPES.BARBELL, type: EXERCISE_TYPES.COMPOUND },
];

/**
 * Flat list of exercise names for autocomplete.
 */
export const EXERCISE_NAMES = COMMON_EXERCISES.map((e) => e.name);

/**
 * Get exercises filtered by muscle group / category.
 * @param {string} category - The category to filter by.
 * @returns {Array} Filtered exercises.
 */
export const getExercisesByCategory = (category) => {
  return COMMON_EXERCISES.filter((e) => e.category === category);
};

/**
 * Get exercises filtered by equipment type.
 * @param {string} equipment - The equipment type to filter by.
 * @returns {Array} Filtered exercises.
 */
export const getExercisesByEquipment = (equipment) => {
  return COMMON_EXERCISES.filter((e) => e.equipment === equipment);
};

/**
 * Calorie burn estimates per minute by workout type.
 * These are rough estimates for calorie calculation.
 */
export const CALORIES_PER_MINUTE = {
  strength: 5,
  cardio: 8,
  flexibility: 3,
  hiit: 10,
  sport: 7,
  other: 5,
};

/**
 * Workout type labels for display.
 */
export const WORKOUT_TYPE_LABELS = {
  strength: 'Strength',
  cardio: 'Cardio',
  flexibility: 'Flexibility',
  hiit: 'HIIT',
  sport: 'Sport',
  other: 'Other',
};

/**
 * Workout type color mapping for badges and charts.
 */
export const WORKOUT_TYPE_COLORS = {
  strength: { main: '#EF4444', bg: 'rgba(239, 68, 68, 0.12)' },
  cardio: { main: '#F97316', bg: 'rgba(249, 115, 22, 0.12)' },
  flexibility: { main: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.12)' },
  hiit: { main: '#EAB308', bg: 'rgba(234, 179, 8, 0.12)' },
  sport: { main: '#3B82F6', bg: 'rgba(59, 130, 246, 0.12)' },
  other: { main: '#6B7280', bg: 'rgba(107, 114, 128, 0.12)' },
};

/**
 * Meal type labels for display.
 */
export const MEAL_TYPE_LABELS = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snack',
  'pre-workout': 'Pre-Workout',
  'post-workout': 'Post-Workout',
};
